import React from 'react';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { advanceFiguresAtom, allBusinessLenderStatusesSelector } from '../../recoil';
import { PRODUCT_CONFIG, ACTION_ENUM } from '../../helpers/productConfig';
import { postRefinanceInterest } from '../../api/vodaLend';
import { useLoader } from '../Loader/useLoader';
import { StaticRoutes } from '../../Router';
import { useTealium } from '../../tealium/useTealium';
import { SEVEN_DAYS_MS } from '../../constants';
import styles from './btaAdvanceBanner.module.css';

const PRODUCT_KEY = 'LLL#BTA';

const shouldShowBanner = ({ applicationStatus, isReadvanceEligible }) => {
  const status = applicationStatus.toLowerCase();
  if (status === 'closed') {
    return true;
  }
  return isReadvanceEligible === true;
};

const resolveButtonDisabled = ({ refinanceDeclinedDate, refinanceRequestedDate }) => {
  if (refinanceDeclinedDate) {
    return true;
  }
  if (refinanceRequestedDate) {
    const requestedAtMs = new Date(refinanceRequestedDate).getTime();
    if (!isNaN(requestedAtMs) && (requestedAtMs + SEVEN_DAYS_MS) > Date.now()) {
      return true;
    }
  }
  return false;
};

export const BTAAdvanceBanner = ({ applicationId, applicationStatus, businessId }) => {
  const data = useRecoilValue(advanceFiguresAtom(applicationId));
  const allBusinessLenderStatuses = useRecoilValue(allBusinessLenderStatusesSelector);
  const navigate = useNavigate();
  const [openLoader, closeLoader] = useLoader();
  const { track, events } = useTealium();

  if (!data) { return null; }

  const normalizedStatus = applicationStatus.toUpperCase();
  const ctaConfig = PRODUCT_CONFIG[PRODUCT_KEY]?.cta?.[normalizedStatus];
  const ctaLabel = ctaConfig?.label ?? 'Request a callback';
  const showBanner = shouldShowBanner({ applicationStatus, isReadvanceEligible: data.isReadvanceEligible });

  if (!showBanner) { return null; }

  const lenderEntry = allBusinessLenderStatuses?.[businessId]?.[PRODUCT_KEY];
  const refinanceRequestedDate = data.refinanceRequestedDate ?? lenderEntry?.refinanceRequestedDate ?? null;
  const refinanceDeclinedDate = data.refinanceDeclinedDate ?? lenderEntry?.refinanceDeclinedDate ?? null;

  const isDisabled = ctaConfig?.disabled ||
    (ctaConfig?.action !== ACTION_ENUM.RE_APPLY_JOURNEY &&
      resolveButtonDisabled({ refinanceDeclinedDate, refinanceRequestedDate }));

  const handleCtaClick = async () => {
    if (isDisabled) { return; }
    track(events.businessTermAdvance.btaBannerClick(ctaLabel));

    if (ctaConfig?.action === ACTION_ENUM.CALLBACK_REQUEST) {
      openLoader();
      try {
        await postRefinanceInterest(businessId, { partnerCode: 'LLL', productCode: 'BTA', applicationId });
      } finally {
        closeLoader();
      }
    } else if (ctaConfig?.action === ACTION_ENUM.RE_APPLY_JOURNEY) {
      navigate(StaticRoutes.FundingApplicationDetails);
    }
  };

  return (
    <div className={styles.banner}>
      <span className={styles.heading}>Need additional funds?</span>
      <span className={styles.description}>
        {normalizedStatus === 'CLOSED'
          ? 'You have paid off enough of your Fixed Term Funding.'
          : 'Request a callback and we\'ll contact you within 24 hours to get you started.'}
      </span>
      <button className={styles.cta} onClick={handleCtaClick} disabled={isDisabled} type='button'>
        {ctaLabel}
      </button>
      <span className={styles.disclaimer}>
        Subject to credit vetting and approval. Ts &amp; Cs apply.
      </span>
    </div>
  );
};

BTAAdvanceBanner.propTypes = {
  applicationId: PropTypes.string.isRequired,
  applicationStatus: PropTypes.string.isRequired,
  businessId: PropTypes.string.isRequired
};
